/* LUVA PDP. Five small jobs, none of them inline.

   1. The thumbnail strip swaps the big image, with arrow keys, aria-selected
      and a live line that tells a screen reader which image is showing now.
   2. The flavour dots write the touched or current flavour's name out in full,
      because a title attribute never shows on a phone.
   3. The quantity stepper keeps the hidden id field's data-qty in sync, because
      that is the attribute assets/luva-cart.js reads when it posts the line,
      and it greys out the minus once the count is at one.
   4. The free delivery line reads the real cart against the real threshold. An
      empty cart gets the plain threshold sentence, not a countdown from zero,
      and the progress track stays hidden until there is progress.
   5. The phone bar carries the primary action whenever the main button is off
      screen, which on a phone includes the moment the page loads. While it is
      open it pays for its own height with padding on the body, so it never
      sits on top of the last line of the page. Armed only below 1000px.

   Without JavaScript the gallery shows the first image, the quantity is one,
   the shipping line is the server-rendered one and the form still submits
   normally, so nothing is lost. */
(function () {
  'use strict';

  var root = document.querySelector('[data-luva-pdp]');
  if (!root) return;

  /* ---------- 1. gallery ---------- */
  var main = root.querySelector('[data-luva-main]');
  var strip = root.querySelector('[data-luva-thumbs]');
  var shotLive = root.querySelector('[data-luva-shot-live]');

  if (main && strip) {
    var tabs = Array.prototype.slice.call(strip.querySelectorAll('[role="tab"]'));

    var select = function (tab, focus) {
      if (!tab) return;
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      var tile = root.querySelector('[data-luva-tile]');
      if (tile) tile.setAttribute('data-mode', tab.getAttribute('data-mode') || 'photo');

      var src = tab.getAttribute('data-src');
      if (src) {
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = src;
        main.alt = tab.getAttribute('data-alt') || '';
        if (shotLive) {
          shotLive.textContent =
            (main.alt || luvaT('pdp_product_image', 'Product image')) + ', ' + luvaT('pdp_image_n_of_total', '{current} of {total}').replace('{current}', tabs.indexOf(tab) + 1).replace('{total}', tabs.length);
        }
      }
      if (focus) tab.focus();
    };

    strip.addEventListener('click', function (e) {
      var tab = e.target.closest('[role="tab"]');
      if (tab) select(tab, false);
    });

    strip.addEventListener('keydown', function (e) {
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      var next = null;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (e.key === 'Home') next = tabs[0];
      else if (e.key === 'End') next = tabs[tabs.length - 1];

      if (next) {
        e.preventDefault();
        select(next, true);
      }
    });
  }

  /* ---------- 2. the flavour name ---------- */
  var flavName = root.querySelector('[data-luva-flavname]');

  if (flavName) {
    var hereName = flavName.textContent;
    var dots = root.querySelectorAll('.luva-pdp__dot');
    var hereHue = root.style.getPropertyValue('--fl');

    /* Touching a flavour paints the stage in that flavour before the visitor
       commits to the click. The colour is the fastest way to say what the
       other side of the link looks like. */
    var say = function (text, hue) {
      flavName.textContent = text || hereName;
      if (hue) root.style.setProperty('--fl', hue);
      else if (hereHue) root.style.setProperty('--fl', hereHue);
    };

    dots.forEach(function (dot) {
      var name = dot.getAttribute('data-name');
      var hue = dot.getAttribute('data-hue');
      /* pointerenter covers mouse, pen and the first tap of a finger. */
      dot.addEventListener('pointerenter', function () { say(name, hue); });
      dot.addEventListener('focus', function () { say(name, hue); });
      dot.addEventListener('pointerleave', function () { say(); });
      dot.addEventListener('blur', function () { say(); });
    });
  }

  /* ---------- 3. quantity ---------- */
  var num = root.querySelector('[data-luva-num]');
  var idField = root.querySelector('[data-luva-idfield]');
  var minus = root.querySelector('[data-luva-step="-1"]');

  var clamp = function (value) {
    var n = parseInt(value, 10);
    if (!n || n < 1) n = 1;
    if (n > 999) n = 999;
    return n;
  };

  var setQty = function (n) {
    if (!num) return;
    num.value = n;
    if (idField) idField.setAttribute('data-qty', n);
    if (minus) minus.disabled = n <= 1;
  };

  if (num) {
    setQty(clamp(num.value));

    root.querySelectorAll('[data-luva-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setQty(clamp(parseInt(num.value, 10) + parseInt(btn.getAttribute('data-luva-step'), 10)));
      });
    });

    num.addEventListener('input', function () {
      if (idField) idField.setAttribute('data-qty', clamp(num.value));
      if (minus) minus.disabled = clamp(num.value) <= 1;
    });

    num.addEventListener('change', function () {
      setQty(clamp(num.value));
    });
  }

  /* ---------- 4. free delivery ---------- */
  var ship = root.querySelector('[data-luva-ship]');
  var track = root.querySelector('[data-luva-track]');
  var progress = root.querySelector('[data-luva-progress]');
  var refresh = function () {};

  if (ship || track) {
    var threshold = parseInt(root.getAttribute('data-threshold'), 10) || 0;
    var format = root.getAttribute('data-money-format') || '{{amount}}';
    var togoText = root.getAttribute('data-togo') || '';
    var reachedText = root.getAttribute('data-reached') || '';
    var cartWord = root.getAttribute('data-cartword') || '';
    var emptyText = root.getAttribute('data-empty') || '';

    /* Shopify hands the money format down as a Liquid style template, so the
       amount has to be rendered the same way the server would render it. */
    var money = function (cents) {
      var value = (cents / 100).toFixed(2);
      var parts = value.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');

      return format
        .replace(/\{\{\s*amount\s*\}\}/g, whole + '.' + parts[1])
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1.') + ',' + parts[1])
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1.'));
    };

    /* Built out of text nodes rather than innerHTML, so a merchant's own
       wording can never turn into markup. */
    var paint = function (total) {
      if (ship) {
        while (ship.firstChild) ship.removeChild(ship.firstChild);

        if (total <= 0) {
          ship.appendChild(document.createTextNode(emptyText));
        } else {
          var left = threshold - total;
          var sum = document.createElement('b');
          sum.textContent = money(total);
          var rest = document.createElement('span');
          rest.textContent = left > 0 ? togoText.replace('[amount]', money(left)) : reachedText;

          if (cartWord) ship.appendChild(document.createTextNode(cartWord + ' '));
          ship.appendChild(sum);
          ship.appendChild(rest);
        }
      }

      if (track) track.hidden = total <= 0;

      if (progress && threshold > 0) {
        var pct = (total / threshold) * 100;
        progress.style.width = (pct > 100 ? 100 : pct) + '%';
      }
    };

    refresh = function () {
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(base + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { paint(cart.total_price); })
        .catch(function () {});
    };

    document.addEventListener('cart:update', refresh);
    document.addEventListener('cart-lines:update', refresh);
  }

  /* ---------- 5. the phone bar ---------- */
  var bar = root.querySelector('[data-luva-bar]');
  var mainAdd = root.querySelector('[data-luva-main-add]');

  if (bar && mainAdd) {
    var barAdd = bar.querySelector('[data-luva-bar-add]');

    if (barAdd) {
      var barLabel = barAdd.textContent.trim();
      var barBusy = barAdd.getAttribute('data-busy') || barLabel;
      var barDone = barAdd.getAttribute('data-done') || barLabel;
      var barReset = null;
      var barWatch = null;

      var barRest = function (text) {
        barAdd.disabled = false;
        barAdd.removeAttribute('aria-busy');
        barAdd.textContent = text;
        if (barReset) clearTimeout(barReset);
        barReset = setTimeout(function () { barAdd.textContent = barLabel; }, 1600);
      };

      barAdd.addEventListener('click', function () {
        if (mainAdd.disabled || mainAdd.hasAttribute('data-luva-notify-cta') || mainAdd.type !== 'submit') return;
        var form = mainAdd.closest('form');
        if (!form) return;

        /* The bar sits outside the form, so assets/luva-cart.js never sees it.
           Its three states are kept here instead: resting, running, and either
           done or back to resting with the error showing in the form. The main
           button is the signal, because luva-cart.js re-enables it either way. */
        barAdd.disabled = true;
        barAdd.setAttribute('aria-busy', 'true');
        barAdd.textContent = barBusy;

        var waited = 0;
        if (barWatch) clearInterval(barWatch);
        barWatch = setInterval(function () {
          waited += 150;
          if (!mainAdd.disabled || waited > 8000) {
            clearInterval(barWatch);
            barWatch = null;
            var msg = form.querySelector('[data-luva-msg]');
            var failed = msg && !msg.hidden && msg.textContent.trim();
            barRest(failed ? barLabel : barDone);
          }
        }, 150);

        if (form.requestSubmit) form.requestSubmit(mainAdd);
        else mainAdd.click();
      });
    }

    var hideTimer = null;

    /* While the bar is up it reserves its own height at the end of the page,
       so the footer is never stuck underneath it. */
    var reserve = function (on) {
      document.body.style.paddingBottom = on ? bar.offsetHeight + 'px' : '';
    };

    var show = function (on) {
      if (on) {
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        bar.hidden = false;
        /* One frame, so the browser has a start value to move away from. */
        requestAnimationFrame(function () {
          bar.setAttribute('data-open', '');
          reserve(true);
        });
      } else if (!bar.hidden) {
        bar.removeAttribute('data-open');
        reserve(false);
        /* A timer rather than transitionend alone, because reduced motion
           removes the transition and the event would then never fire. */
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(function () {
          hideTimer = null;
          if (!bar.hasAttribute('data-open')) bar.hidden = true;
        }, 280);
      }
    };

    /* Phone only: desktop hides the bar with CSS, so skip the observer and
       avoid toggling body padding on wide screens. */
    var phone = window.matchMedia('(max-width: 999px)');
    var io = null;
    var arm = function () {
      if (!phone.matches) {
        if (io) { io.disconnect(); io = null; }
        show(false);
        reserve(false);
        return;
      }
      if (io || !('IntersectionObserver' in window)) return;
      /* The main button is below the first screen on a phone, so the bar has
         to be up from the start. It steps back the moment the real button is
         on screen, and returns as soon as it has gone again. */
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            show(!entry.isIntersecting);
          });
        },
        { root: null, rootMargin: '0px 0px -64px 0px', threshold: 0 }
      );
      io.observe(mainAdd);
    };
    arm();
    if (phone.addEventListener) phone.addEventListener('change', arm);
    else if (phone.addListener) phone.addListener(arm);
  }
})();
